import { useElementSize } from './useElementSize';

interface ChartDatum {
  label: string;
  value: number | null;
}

/**
 * One month per point, best-of-category on the y axis. The per-exercise view has
 * a continuous date axis and its own marks, so it lives in ExerciseHistoryChart
 * rather than being a mode of this one.
 */
export interface LiftTrendChartProps {
  title: string;
  color: string;
  data: ChartDatum[];
  /** Caption at the top-right of the chart. */
  unitLabel?: string;
}

// Fixed pixel paddings inside the plot; the plot itself sizes to its container
// so charts squish to fit the available height instead of forcing a scroll.
const PAD_L = 40;
const PAD_R = 14;
const PAD_T = 16;
const PAD_B = 20;

export function LiftTrendChart({
  title,
  color,
  data,
  unitLabel = 'equivalent 1RM (lb)',
}: LiftTrendChartProps) {
  const [plotRef, { width: w, height: h }] = useElementSize<HTMLDivElement>();

  const values = data.map((d) => d.value).filter((v): v is number => v !== null);
  const hasData = values.length > 0;

  const ariaLabel = hasData
    ? `${title} ${unitLabel.replace(/\s*\(lb\)$/, '')} by month: ${data
        .filter((d) => d.value !== null)
        .map((d) => `${d.label} ${Math.round(d.value as number)} pounds`)
        .join(', ')}.`
    : `${title}: no data yet.`;

  const ready = hasData && w > 0 && h > 0;

  const renderPlot = () => {
    const n = data.length;
    const plotW = Math.max(1, w - PAD_L - PAD_R);
    const plotH = Math.max(1, h - PAD_T - PAD_B);
    const xFor = (i: number) => (n <= 1 ? PAD_L + plotW / 2 : PAD_L + (i / (n - 1)) * plotW);

    let min = Math.min(...values);
    let max = Math.max(...values);
    if (min === max) {
      const pad = Math.max(10, Math.round(max * 0.1));
      min = Math.max(0, min - pad);
      max = max + pad;
    } else {
      const pad = (max - min) * 0.18;
      min = Math.max(0, min - pad);
      max = max + pad;
    }
    const yFor = (v: number) => PAD_T + (1 - (v - min) / (max - min)) * plotH;

    // Line segments split on null gaps.
    const segments: { i: number; x: number; y: number }[][] = [];
    let current: { i: number; x: number; y: number }[] = [];
    data.forEach((d, i) => {
      if (d.value === null) {
        if (current.length) segments.push(current);
        current = [];
      } else {
        current.push({ i, x: xFor(i), y: yFor(d.value) });
      }
    });
    if (current.length) segments.push(current);
    const markers = segments.flat();

    // Selective direct labels: latest value + peak (never every point).
    let latestIdx = -1;
    for (let i = data.length - 1; i >= 0; i--) {
      if (data[i].value !== null) {
        latestIdx = i;
        break;
      }
    }
    let peakIdx = -1;
    let peakVal = -Infinity;
    data.forEach((d, i) => {
      if (d.value !== null && d.value > peakVal) {
        peakVal = d.value;
        peakIdx = i;
      }
    });
    const labelIdxs = new Set<number>();
    if (latestIdx >= 0) labelIdxs.add(latestIdx);
    if (peakIdx >= 0) labelIdxs.add(peakIdx);

    // Gridline count scales with the plot's height so a short panel stays clean
    // and a tall one (the single-exercise view) doesn't read as empty space.
    const gridLines = Math.min(6, Math.max(3, Math.round(plotH / 70) + 1));
    const gridVals = Array.from(
      { length: gridLines },
      (_, i) => max - ((max - min) * i) / (gridLines - 1),
    );

    return (
      <svg
        className="trend-chart__svg"
        viewBox={`0 0 ${w} ${h}`}
        width={w}
        height={h}
        role="img"
        aria-label={ariaLabel}
      >
        {gridVals.map((v, idx) => {
          const y = yFor(v);
          return (
            <g key={`grid-${idx}`}>
              <line x1={PAD_L} y1={y} x2={w - PAD_R} y2={y} className="trend-chart__grid" />
              <text x={PAD_L - 6} y={y + 3} className="trend-chart__ylabel" textAnchor="end">
                {Math.round(v)}
              </text>
            </g>
          );
        })}

        {segments.map((seg, idx) => (
          <polyline
            key={`seg-${idx}`}
            fill="none"
            stroke={color}
            strokeWidth={2}
            strokeLinejoin="round"
            strokeLinecap="round"
            points={seg.map((p) => `${p.x},${p.y}`).join(' ')}
          />
        ))}

        {markers.map((p) => (
          <circle
            key={`mk-${p.i}`}
            cx={p.x}
            cy={p.y}
            r={4}
            fill={color}
            stroke="var(--color-paper-light)"
            strokeWidth={2}
          />
        ))}

        {[...labelIdxs].map((i) => {
          const v = data[i].value as number;
          const x = xFor(i);
          const y = yFor(v);
          const anchor = i === 0 ? 'start' : i === n - 1 ? 'end' : 'middle';
          return (
            <text
              key={`val-${i}`}
              x={x}
              y={Math.max(11, y - 9)}
              textAnchor={anchor}
              className="trend-chart__vallabel"
            >
              {Math.round(v)}
            </text>
          );
        })}

        {data.map((d, i) => (
          <text
            key={`x-${i}`}
            x={xFor(i)}
            y={h - 6}
            textAnchor="middle"
            className="trend-chart__xlabel"
          >
            {d.label}
          </text>
        ))}
      </svg>
    );
  };

  return (
    <figure className="trend-chart">
      <figcaption className="trend-chart__title">
        <span
          className="trend-chart__swatch"
          style={{ backgroundColor: color }}
          aria-hidden="true"
        />
        {title}
        <span className="trend-chart__unit">{unitLabel}</span>
      </figcaption>

      {!hasData ? (
        <div className="trend-chart__empty">No {title.toLowerCase()} lifts logged yet.</div>
      ) : (
        <div className="trend-chart__plot" ref={plotRef}>
          {ready && renderPlot()}
        </div>
      )}
    </figure>
  );
}
